import { RemoteTable } from '@neo/blocks/data-table/data-table'
import type { DataTableFilterField } from '@neo/blocks/data-table/types'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@neo/primitives/alert-dialog'
import { Button } from '@neo/primitives/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@neo/primitives/dropdown-menu'
import type { ColumnDef } from '@tanstack/react-table'
import { Check, MoreVertical, Plus, X } from 'lucide-react'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useParams } from 'router-v6'
import { formatDateTime } from 'v2/api-console/shared/utils/date-utils'
import { flowService } from 'v2/shared/services/flow-service'
import { flowQueryKeys } from 'v2/shared/utils/query-key-management'
import { errorToast } from 'v2/utils/toasts'
import { KbStatusBadge } from '../knowledge-base/kb-status-badge'
import { useGetKnowledgeBases, useKBMutations } from '../knowledge-base/queries'
import type { RKnowledgeBase } from '../knowledge-base/types'
import { CreateKBInline } from './create-kb-inline'
import { EditKBInline } from './edit-kb-inline'
import { LinkKnowledgeBasePopover } from './link-knowledge-base-popover'
import { useLinkKnowledgeBaseMutation } from './mutations'

export const AgentKnowledgeBaseTab = () => {
  const { id = '' } = useParams()
  const [view, setView] = useState<'list' | 'create' | 'edit'>('list')
  const [editKbId, setEditKbId] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<RKnowledgeBase | null>(null)

  // KBs currently linked to THIS agent (kb.phlo_uuids includes the agent id) — the source of truth
  // for an Unlink: we resend the linked set minus the one being removed (Delete removes the KB
  // entirely; Unlink only detaches it from this agent).
  const { data: allKbs, isSuccess: kbsLoaded } = useGetKnowledgeBases()
  const linkedIds = useMemo(
    () =>
      (allKbs ?? []).reduce<string[]>((ids, kb) => {
        if (kb.phlo_uuids?.includes(id)) ids.push(kb.id)
        return ids
      }, []),
    [allKbs, id],
  )

  // When the agent-link reverse-read failed, kb.phlo_uuids (and thus linkedIds) is non-authoritative.
  // An Unlink resends `linkedIds` minus one — full-replacing knowledge_base_ids — so a wrongly-empty
  // set would silently drop the agent's other KB links. Block unlink until the links load.
  const linksUnavailable = (allKbs ?? []).some((kb) => kb.linksUnavailable)

  const { deleteKB, toggleStatus } = useKBMutations()
  const { mutate: linkKnowledgeBases, isPending: linkPending } = useLinkKnowledgeBaseMutation()

  // Selecting a kebab item must not also open the row (onRowClick → Edit). Radix selects on
  // pointerup and the item unmounts before our onClick can stopPropagation, so the trailing click
  // reaches the row. We flag a short window around a menu selection and have onRowClick ignore it.
  const suppressRowClickRef = useRef(false)
  const suppressNextRowClick = useCallback(() => {
    suppressRowClickRef.current = true
    setTimeout(() => {
      suppressRowClickRef.current = false
    }, 300)
  }, [])

  const handleUnlink = useCallback(
    (kbId: string) => {
      // Unlink resends `linkedIds` minus one as a FULL replace, so it must never run against a
      // non-authoritative set. Block when:
      //  - !kbsLoaded: allKbs is undefined while the gcTime:0 query loads or after it
      //    errors → linkedIds computes to [] → an unlink would send [] and detach EVERY KB. Only act
      //    once the reverse-read has actually succeeded (kebab parity with the isLoading-disabled popover).
      //  - linksUnavailable: the per-row reverse-read (phlo_uuids) failed → linkedIds is untrustworthy.
      //  - linkPending: a link mutation is already in flight → two quick unlinks would both derive from
      //    the same pre-refetch snapshot and the 2nd would resend the 1st's just-removed id. Serialize.
      if (!kbsLoaded || linksUnavailable || linkPending) {
        errorToast({
          title: "Couldn't unlink",
          description: linkPending
            ? 'Please wait for the previous change to finish, then try again.'
            : "The agent's current Knowledge Bases aren't loaded yet. Refresh and try again.",
        })
        return
      }
      linkKnowledgeBases({ knowledgeBaseIds: linkedIds.filter((x) => x !== kbId), flowId: id })
    },
    [linkedIds, id, linkKnowledgeBases, linksUnavailable, kbsLoaded, linkPending],
  )

  const columns = useMemo<ColumnDef<RKnowledgeBase>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Knowledge Base Name',
        meta: { width: '36%' },
      },
      {
        accessorKey: 'embedding_status',
        // Was "Status", which read as a synonym of the old "State" (enabled) badge and confused people.
        // This column is purely ingest readiness (Syncing/Ready/Failed); the enabled flag is now the
        // "Enabled" tick/cross at the end. A failed/partial KB shows WHY on hover.
        header: 'Sync Status',
        cell: ({ row }) => (
          <KbStatusBadge
            status={row.original.embedding_status}
            errorDetail={row.original.error_detail}
          />
        ),
      },
      {
        accessorKey: 'phlo_uuids',
        header: 'Associated Agents',
        cell: ({ getValue }) => {
          const phloUuids = getValue() as string[]
          return `${phloUuids?.length || 0} Agents`
        },
      },
      {
        accessorKey: 'updated_at',
        header: 'Last Synced On',
        cell: ({ getValue }) => {
          const value = getValue() as string
          return (
            <span className="font-jetbrains text-xs-400 tabular-nums">
              {value ? formatDateTime(value) : '-'}
            </span>
          )
        },
      },
      {
        // Enabled is a clean tick/cross (a disabled KB is excluded from retrieval) — much clearer at a
        // glance than the old "State: Active/Disabled" badge that looked like a second status.
        accessorKey: 'enabled',
        header: 'Enabled',
        meta: { width: '80px' },
        cell: ({ getValue }) =>
          (getValue() as boolean) ? (
            <Check className="h-4 w-4 text-status-success" aria-label="Enabled" />
          ) : (
            <X className="h-4 w-4 text-muted-foreground" aria-label="Disabled" />
          ),
      },
      {
        id: 'actions',
        header: '',
        meta: { width: '48px' },
        cell: ({ row }) => {
          const kb = row.original
          return (
            <div className="flex justify-end">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    // stop the click bubbling to the row's onRowClick (which opens Edit)
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`button-kb-actions-${kb.id}`}
                  >
                    <MoreVertical size={18} />
                  </Button>
                </DropdownMenuTrigger>
                {/* stopPropagation: React synthetic events bubble through the portal's component
                    tree to the row's onRowClick (which opens the KB docs page), so an item click
                    would both act AND navigate. Stop it at each item. */}
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onSelect={() => {
                      suppressNextRowClick()
                      handleUnlink(kb.id)
                    }}
                    data-testid="action-unlink-kb"
                  >
                    Unlink from agent
                  </DropdownMenuItem>
                  {/* Enable/Disable the KB. A disabled KB can't be linked to agents and its content is
                      excluded from retrieval (rag honors `enabled`). Label reflects the next action. */}
                  <DropdownMenuItem
                    onSelect={() => {
                      suppressNextRowClick()
                      toggleStatus.mutate({ id: kb.id, enabled: !kb.enabled })
                    }}
                    data-testid="action-toggle-kb-enabled"
                  >
                    {kb.enabled ? 'Disable' : 'Enable'}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() => {
                      suppressNextRowClick()
                      setDeleteTarget(kb)
                    }}
                    className="text-destructive focus:text-destructive"
                    data-testid="action-delete-kb"
                  >
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )
        },
      },
    ],
    [handleUnlink, suppressNextRowClick, toggleStatus],
  )

  const filterFields = useMemo<DataTableFilterField<RKnowledgeBase>[]>(
    () => [
      {
        label: 'Name',
        value: 'name',
        paramKey: 'name',
        placeholder: 'Search',
      },
    ],
    [],
  )

  if (view === 'create') {
    return <CreateKBInline onBack={() => setView('list')} flowId={id} />
  }

  if (view === 'edit' && editKbId) {
    return (
      <EditKBInline
        kbId={editKbId}
        onBack={() => {
          setEditKbId(null)
          setView('list')
        }}
      />
    )
  }

  return (
    <div className="h-full flex flex-col">
      <RemoteTable
        clientTable
        queryKey={[...flowQueryKeys().knowledgeBases, id]}
        staleTime={0}
        columns={columns}
        fetchService={flowService.listKnowledgeBases}
        filterFields={filterFields}
        ActionButton={
          <div className="flex gap-2">
            <LinkKnowledgeBasePopover />
            <Button onClick={() => setView('create')} data-testid="button-create-knowledge-base">
              <Plus size={16} className="mr-2" />
              Create Knowledge Base
            </Button>
          </div>
        }
        extractData={(data) => {
          // Show ONLY the KBs linked to THIS agent — and count the SAME filtered set. Counting
          // data.length (all org KBs) made the header say e.g. "6 knowledge bases" while only 2 were
          // attached.
          const linked = data.filter((d) => d.phlo_uuids.includes(id))
          return { data: linked, totalCount: linked.length }
        }}
        onRowClick={(row) => {
          if (suppressRowClickRef.current) return // a kebab action was just selected
          setEditKbId(row.id)
          setView('edit')
        }}
      />
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete knowledge base?</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleteTarget?.name}" will be permanently deleted and removed from every agent that
              uses it. This action can't be undone. To only detach it from this agent, use "Unlink
              from agent" instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete-kb">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (deleteTarget) deleteKB.mutate(deleteTarget.id)
                setDeleteTarget(null)
              }}
              data-testid="button-confirm-delete-kb"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
